import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

const PagosCobranzas = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [tipoMovimiento, setTipoMovimiento] = useState('cobro');

  const movimientosData = [
    { id: 1, fecha: '03/02/2026', tipo: 'Cobro', cliente: 'YPF S.A.', factura: 'A-0001-00001234', monto: 2500000, estado: 'Cobrado' },
    { id: 2, fecha: '02/02/2026', tipo: 'Pago', proveedor: 'Servicios HSE SRL', factura: 'FC-4521', monto: 1250000, estado: 'Pagado' },
    { id: 3, fecha: '01/02/2026', tipo: 'Cobro', cliente: 'Pan American Energy', factura: 'A-0001-00001198', monto: 1800000, estado: 'Pendiente' },
    { id: 4, fecha: '31/01/2026', tipo: 'Pago', proveedor: 'Transporte Petrolero', factura: 'FC-8832', monto: 680000, estado: 'Pagado' },
    { id: 5, fecha: '30/01/2026', tipo: 'Cobro', cliente: 'Tecpetrol', factura: 'A-0001-00001156', monto: 3200000, estado: 'Vencido' },
  ];

  const resumenStats = [
    { label: 'Por Cobrar', value: '$18.3M', icon: <TrendingUpIcon />, color: theme.palette.success.main },
    { label: 'Por Pagar', value: '$9.7M', icon: <TrendingDownIcon />, color: theme.palette.error.main },
    { label: 'Cobrado (Feb)', value: '$4.3M', icon: <ReceiptIcon />, color: theme.palette.info.main },
    { label: 'Pagado (Feb)', value: '$1.9M', icon: <ReceiptIcon />, color: theme.palette.warning.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Pagos y Cobranzas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión de cuentas por cobrar y pagar
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<TrendingDownIcon />} onClick={() => { setTipoMovimiento('pago'); setOpenDialog(true); }}>
            Registrar Pago
          </Button>
          <Button variant="contained" startIcon={<TrendingUpIcon />} onClick={() => { setTipoMovimiento('cobro'); setOpenDialog(true); }}>
            Registrar Cobro
          </Button>
        </Box>
      </Box>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabla */}
      <Card>
        <CardContent>
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
            <Tab label="Todos" />
            <Tab label="Por Cobrar" />
            <Tab label="Por Pagar" />
            <Tab label="Vencidos" />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Cliente/Proveedor</TableCell>
                  <TableCell>Factura</TableCell>
                  <TableCell align="right">Monto</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movimientosData.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.fecha}</TableCell>
                    <TableCell>
                      <Chip
                        icon={row.tipo === 'Cobro' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                        label={row.tipo}
                        size="small"
                        color={row.tipo === 'Cobro' ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.cliente || row.proveedor}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.factura}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        ${row.monto.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.estado}
                        size="small"
                        color={
                          row.estado === 'Cobrado' || row.estado === 'Pagado'
                            ? 'success'
                            : row.estado === 'Vencido'
                            ? 'error'
                            : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      {row.estado === 'Pendiente' && (
                        <Button size="small" variant="outlined">
                          {row.tipo === 'Cobro' ? 'Cobrar' : 'Pagar'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Registrar {tipoMovimiento === 'cobro' ? 'Cobro' : 'Pago'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{tipoMovimiento === 'cobro' ? 'Cliente' : 'Proveedor'}</InputLabel>
                <Select label={tipoMovimiento === 'cobro' ? 'Cliente' : 'Proveedor'}>
                  <MenuItem value="1">YPF S.A.</MenuItem>
                  <MenuItem value="2">Pan American Energy</MenuItem>
                  <MenuItem value="3">Tecpetrol</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Número de Factura" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Monto" type="number" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Fecha" type="date" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Cuenta Bancaria</InputLabel>
                <Select label="Cuenta Bancaria">
                  <MenuItem value="1">Banco Nación - ****4521</MenuItem>
                  <MenuItem value="2">Banco Galicia - ****7832</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Forma de Pago</InputLabel>
                <Select label="Forma de Pago">
                  <MenuItem value="transferencia">Transferencia</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="efectivo">Efectivo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PagosCobranzas;
